import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {Apis} from './apis';
import {ApiDetail} from './api-detail';
import {KeyItem} from './key-item';
import {Applications} from './applications';
import {ApplicationItem} from './application-item';
import {ApplicationDetail} from './application-detail';
import {SubscriptionItem} from './subscription-item';
import {Subscriptions} from './subscriptions';
import {ConfigurationItem} from '../core/configuration-item';
import {ConfigurationService} from '../core/configuration.service';

const VERSION_PREFIX = '/v';

@Injectable({
  providedIn: 'root'
})
export class ApisService {
  private apiHost = 'http://localhost:9001';
  private oauthTokenUri: string;
  private gatewayBaseUri: string;
  private resourceApis: string;
  private resourceSubscriptions: string;
  private resourceApplications: string;
  private resourceCheckEntitlements: string;
  private resourceTokens: string;

  constructor(private configurationService: ConfigurationService, private http: HttpClient) {
    this.getConfiguration();
  }

  private getConfiguration() {
    const configuration = this.configurationService.getConfiguration();
    const apiManagerConfiguration: ConfigurationItem = configuration.apiManager;
    this.gatewayBaseUri = apiManagerConfiguration.gatewayBaseUri;
    this.oauthTokenUri = apiManagerConfiguration.oauthTokenUri;

    this.apiHost = apiManagerConfiguration.scheme + '://' + apiManagerConfiguration.host;
    if (apiManagerConfiguration.port) {
      this.apiHost += ':' + apiManagerConfiguration.port;
    }
    this.createFullyQualifiedResourceNames();
  }

  private createFullyQualifiedResourceNames() {
    this.resourceApis = this.apiHost + '/apis';
    this.resourceSubscriptions = this.apiHost + '/subscriptions';
    this.resourceApplications = this.apiHost + '/applications';
    this.resourceCheckEntitlements = this.apiHost + '/check-entitlements';
    this.resourceTokens = this.apiHost + '/tokens';
  }

  // getApis(): Observable<Api[]> {
  //   return (this.http.get<Api[]>(this.resourceApis, {withCredentials: true}));
  // }

  checkEntitlements(): Observable<any> {
    const options = {withCredentials: true};
    return (this.http.get<any>(this.resourceCheckEntitlements, options));
  }

  getApis(params?): Observable<Apis> {
    const options = {withCredentials: true, params};
    return (this.http.get<Apis>(this.resourceApis, options));
  }

  getApi(apiId): Observable<ApiDetail> {
    return this.http.get<ApiDetail>(this.resourceApis + '/' + apiId, {withCredentials: true});
  }

  getThumbnailUri(api) {
    return (api.thumbnailUri ? this.apiHost + api.thumbnailUri : (api.thumbnailUrl ? this.apiHost + api.thumbnailUrl : undefined));
  }

  getOauthTokenUri() {
    return (this.oauthTokenUri);
  }

  getOauthTokenUriHasPort() {
    return this.oauthTokenUri.includes(':');
  }

  getGatewayBaseUri() {
    return (this.gatewayBaseUri);
  }

  getGatewayBaseUriHasPort() {
    return this.gatewayBaseUri.includes(':');
  }

  getSubscriptionsForApi(apiId): Observable<Subscriptions> {
    const uri = this.resourceSubscriptions + this.buildQueryParameterString([{name: 'apiId', value: apiId}]);
    return this.http.get<Subscriptions>(uri, {withCredentials: true});
    // .pipe(
    //   catchError(this.handleError<Subscriptions>('getSubscriptionsForApi'))
    // );
  }

  getSubscriptionsForApplication(applicationId): Observable<Subscriptions> {
    const uri = this.resourceSubscriptions + this.buildQueryParameterString([{name: 'applicationId', value: applicationId}]);
    return this.http.get<Subscriptions>(uri, {withCredentials: true});
  }

  getSubscriptionDetail(subscriptionId): Observable<SubscriptionItem> {
    const uri = this.resourceSubscriptions + '/' + subscriptionId;
    return this.http.get<SubscriptionItem>(uri, {withCredentials: true});
  }

  generateAccessToken(consumerKey, consumerSecret, grantType, username?, password?, refreshToken?) {
    const credentialsBase64 = btoa(consumerKey + ':' + consumerSecret);
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded');
    let httpParams = new HttpParams()
      .set('grant_type', grantType)
      .set('client_id', consumerKey)
      .set('client_secret', consumerSecret);
    if (username && password) {
      httpParams = httpParams
        .set('username', username)
        .set('password', password);
    }
    if (refreshToken) {
      httpParams = httpParams.set('refresh_token', refreshToken);
    }
    const httpOptions = {
      withCredentials: true,
      headers
    };
    return this.http.post<any>(this.resourceTokens, httpParams, httpOptions);
  }

  createSubscription(requestBody: object): Observable<object> {
    const uri = this.resourceSubscriptions;
    return this.http.post<SubscriptionItem>(uri, requestBody, {withCredentials: true});
  }

  deleteSubscription(subscriptionId): Observable<SubscriptionItem> {
    const uri = this.resourceSubscriptions + '/' + subscriptionId;
    return this.http.delete<SubscriptionItem>(uri, {withCredentials: true});
  }

  getApplicationDetail(applicationId): Observable<ApplicationDetail> {
    const uri = this.resourceApplications + '/' + applicationId;
    return this.http.get<ApplicationDetail>(uri, {withCredentials: true});
  }

  getApplications(): Observable<Applications> {
    return this.http.get<Applications>(this.resourceApplications, {withCredentials: true});
  }

  createApplication(requestBody: object): Observable<object> {
    return this.http.post<Applications>(this.resourceApplications, requestBody, {withCredentials: true});
  }

  createApplicationKeys(applicationId: string, requestBody: object): Observable<object> {
    const uri = this.resourceApplications + '/generate-keys' +
      this.buildQueryParameterString([{name: 'applicationId', value: applicationId}]);
    return this.http.post<Applications>(uri, requestBody, {withCredentials: true});
  }

  buildQueryParameterString(queryParameters) {
    let queryParameterString;
    queryParameters.forEach((queryParameter) => {
      if (queryParameterString) {
        queryParameterString += '&';
      } else {
        queryParameterString = '?';
      }
      queryParameterString += queryParameter.name + '=' + encodeURIComponent(queryParameter.value);
    });

    return (queryParameterString);
  }

  enrichApis(apis) {
    apis.forEach((api) => {
      this.enrichApi(api);
    });
  }

  enrichApi(api) {
    api.displayName = this.getDisplayNameFromApi(api);
    api.context = this.correctContext(api.context);
  }

  getDisplayNameFromApi(api) {
    return this.getDisplayName(api.name);
  }

  getDisplayName(name) {
    let displayName = name[0];
    let previousIsLower: boolean = this.isLowerCaseAt(name, 0);
    for (let i = 1; i < name.length; ++i) {
      let currentIsLower = this.isLowerCaseAt(name, i);
      if (previousIsLower && !currentIsLower) {
        displayName += ' ';
      }
      displayName += name[i];
      previousIsLower = currentIsLower;
    }
    return (displayName);
  }

  getDisplayNameFromApiIdentifier(apiIdentifier) {
    // admin-CCAMP-v1
    let name: string;
    const parts = apiIdentifier.split('-');
    if (parts.length > 2) { // valid apiIdentifier is composed of 3 parts, but trying to account for more dashes in the middle
      parts.splice(parts.length - 1, 1); // remove last element
      parts.splice(0, 1); // remove first element
      name = parts.join('-'); // squish them all back together
    }
    return this.getDisplayName(name) || apiIdentifier;
  }

  isLowerCaseAt(str, index) {
    return str.charAt(index).toLowerCase() === str.charAt(index);
  }

  correctContext(context) {
    const pos = context.indexOf(VERSION_PREFIX);
    if (pos > -1) {
      context = context.substr(0, pos);
    }
    return (context);
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
